// @ts-nocheck
// Tela de Landing de Convite — abre quando o usuário acessa /c/:token.
// Usuário deslogado: vê preview do clube + CTA "Criar conta" ou "Entrar".
// Usuário logado: aceita imediatamente (ou clica pra confirmar).

import React, { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { api } from '../../services/apiV2';
import { useTerm, ClubBanner, UsersIcon } from './components';
import { OpenInAppBanner } from '../open-in-app';
import { AuthSession } from '../../types';

export const InviteLandingScreen: React.FC<{
  token: string;
  session: AuthSession | null;
  onLoginRequest: (inheritedTerm: 'clube' | 'grupo') => void;
  onSignupRequest: (inheritedTerm: 'clube' | 'grupo') => void;
  onAccepted: (clubId: string) => void;
}> = ({ token, session, onLoginRequest, onSignupRequest, onAccepted }) => {
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getInvitePreview(token);
        if (!cancelled) setPreview(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Convite inválido');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const inheritedTerm = (preview?.inviter?.preferredTerm as 'clube' | 'grupo') || 'clube';
  const T = useTerm(inheritedTerm);

  const handleAccept = async () => {
    if (!session) return;
    setAccepting(true);
    try {
      const r = await api.acceptInvite(session, token);
      onAccepted(r.clubId);
    } catch (e: any) {
      setError(e?.message || 'Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-3)' }}>
        Carregando convite…
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div style={{ padding: '60px 20px' }}>
        <div className="serif" style={{ fontSize: 28, lineHeight: 1.05 }}>
          Convite indisponível
        </div>
        <div style={{ color: 'var(--ink-3)', fontSize: 14, marginTop: 12 }}>
          {error || 'Esse convite não está mais válido.'}
        </div>
      </div>
    );
  }

  const club = preview.club;
  const inviter = preview.inviter;

  return (
    <div className="scroll" style={{ paddingBottom: 40 }}>
      <ClubBanner hue={club.cover.hue} style={club.cover.template} height={220}
        bannerUrl={(club.cover as any).bannerUrl || null} coverUrl={club.cover.customUrl || null}
        label={
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 16 }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(243,236,222,0.85)', marginBottom: 6,
            }}>
              {inviter?.name ? `${inviter.name} te convidou` : 'Você foi convidado'}
            </div>
            <div className="serif" style={{
              fontSize: 32, lineHeight: 1.05, color: 'white',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
            }}>
              {club.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <UsersIcon size={14} color="rgba(255,255,255,0.85)"/>
              <span className="mono" style={{
                fontSize: 11, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.06em',
              }}>
                {club.memberCount} {club.memberCount === 1 ? 'membro' : 'membros'} · {T.s} {club.privacy === 'open' ? 'público' : 'fechado'}
              </span>
            </div>
          </div>
        }/>

      <div style={{ padding: '24px 20px 0' }}>
        {club.description && (
          <div className="serif" style={{
            fontSize: 16, lineHeight: 1.5, color: 'var(--ink-2)',
            fontStyle: 'italic', textWrap: 'pretty',
          }}>
            "{club.description}"
          </div>
        )}

        <div style={{
          marginTop: 24, padding: '16px 18px',
          background: 'var(--paper-2)',
          border: '1px solid var(--line)',
          borderRadius: 16,
        }}>
          <div className="mono" style={{
            fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--ink-3)', fontWeight: 600,
          }}>O que é um {T.s}?</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-2)', marginTop: 6 }}>
            Leitura compartilhada com ranking, encontros e estudo do livro coletivo.
            Você lê no seu ritmo, mas com pessoas ao seu redor — no mesmo livro ou cada um no seu.
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '28px 20px 0' }}>
        {session ? (
          <button onClick={handleAccept} disabled={accepting} style={{
            width: '100%', padding: '16px', borderRadius: 16,
            background: accepting ? 'var(--paper-3)' : 'var(--ember)',
            color: accepting ? 'var(--ink-4)' : 'var(--paper)',
            fontSize: 15, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: accepting ? 'none' : '0 14px 28px -10px rgba(199,83,26,0.55)',
          }}>
            {accepting ? 'Aceitando…' : `Entrar no ${T.s}`}
            {!accepting && <Icon name="arrow-right" size={16} color="var(--paper)"/>}
          </button>
        ) : (
          <>
            <button onClick={() => onSignupRequest(inheritedTerm)} style={{
              width: '100%', padding: '16px', borderRadius: 16,
              background: 'var(--ember)', color: 'var(--paper)',
              fontSize: 15, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 14px 28px -10px rgba(199,83,26,0.55)',
            }}>
              Criar conta e entrar
              <Icon name="arrow-right" size={16} color="var(--paper)"/>
            </button>
            <button onClick={() => onLoginRequest(inheritedTerm)} style={{
              width: '100%', padding: '14px', marginTop: 8,
              background: 'transparent', color: 'var(--ink-2)',
              fontSize: 14, fontWeight: 500,
            }}>
              Já tenho conta · entrar
            </button>
          </>
        )}
      </div>

      <OpenInAppBanner path={'/c/' + token} />
    </div>
  );
};
